import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { HttpError, HttpResponse } from '@shared/utils/models/http.model';
import {
  switchMap,
  throwError,
  catchError,
  of,
  map,
  retry,
  Observable,
  forkJoin,
  interval,
  first,
  mergeMap,
} from 'rxjs';
import { LogoutActions, UserActions } from '../actions/user.actions';
import { selectUser } from '../selectors/user.selectors';
import { AuthService } from '@features/auth/data-access/services/auth.service';
import { UserService } from '../services/user.service';
import { IPLocation, Notification, UserProfile } from '@core/utils/models/user.model';
import { OfferActions } from '@features/admin/data-access/actions/offer.action';
import { DemandActions } from '@features/admin/data-access/actions/demand.action';

@Injectable()
export class UserEffects {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LogoutActions.logout),
      concatLatestFrom(() => this.store.select(selectUser)),
      switchMap(([_, user]) => {
        if (!user) {
          return throwError(() => 'User data does not exist in state');
        }
        return this.authService.logout(user.id).pipe(
          switchMap(() => [LogoutActions.logoutSuccess(), UserActions.clearUser()]),
          catchError((error: HttpError) => of(LogoutActions.logoutFailure({ error })))
        );
      }),
      catchError((error: HttpError) => of(LogoutActions.logoutFailure({ error })))
    );
  });

  getProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.getProfile, UserActions.clearUser),
      switchMap((action) =>
        action.type === UserActions.clearUser.type
          ? of(UserActions.resetProfileHttpState())
          : this.userService.getUserProfile().pipe(
              map((response: HttpResponse<[UserProfile]>) =>
                UserActions.getProfileSuccess({ profile: response.message[0] })
              ),
              retry({ count: 5, delay: 5000 }),
              catchError((error: HttpError) => of(UserActions.getProfileFailure({ error })))
            )
      )
    );
  });

  updateProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateProfile),
      switchMap((action) => {
        const requests$: Observable<unknown>[] = [];
        const { avatar, ...rest } = action.payload;
        if (avatar) {
          requests$.push(this.userService.uploadAvatar(avatar));
        }
        if (Object.keys(rest).length > 0) {
          requests$.push(
            interval(avatar ? 4000 : 0).pipe(
              first(),
              switchMap(() => this.userService.updateUserProfile(rest))
            )
          );
        }
        return forkJoin(requests$).pipe(
          switchMap(() => this.userService.getUserProfile()),
          map((response: HttpResponse<[UserProfile]>) =>
            UserActions.updateProfileSuccess({ profile: response.message[0] })
          ),
          catchError((error: HttpError) => of(UserActions.updateProfileFailure({ error })))
        );
      })
    );
  });

  getUserIPLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.getUserIPLocation, UserActions.cancelUserActions),
      switchMap((action) =>
        action.type === UserActions.cancelUserActions.type
          ? of(UserActions.cancelUserActionsSuccess())
          : this.userService.getUserIPLocation().pipe(
              map((response: IPLocation) => UserActions.getUserIPLocationSuccess({ location: response })),
              catchError((error: HttpError) => of(UserActions.getUserIPLocationFailure({ error })))
            )
      )
    );
  });

  getNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserActions.getNotifications,
        OfferActions.markAllOffersAsSeenSuccess,
        DemandActions.markAllDemandsAsSeenSuccess,
        OfferActions.markAllOfferInterestsAsSeenSuccess,
        DemandActions.markAllDemandInterestsAsSeenSuccess,
        UserActions.updateProfileSuccess,
        UserActions.clearUser
      ),
      switchMap((action) =>
        action.type === UserActions.clearUser.type
          ? of(UserActions.resetNotificationsHttpState())
          : this.userService.getUserNotifications().pipe(
              map((response: HttpResponse<{ total: number; messages: Notification[] }>) =>
                UserActions.getNotificationsSuccess({
                  unread: response.message.total,
                  notifications: response.message.messages,
                })
              ),
              retry({ count: 5, delay: 5000 }),
              catchError((error: HttpError) => of(UserActions.getNotificationsFailure({ error })))
            )
      )
    );
  });

  markAnnouncementAsSeen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.markAnnouncementAsSeen),
      mergeMap((action) =>
        this.userService.markAnnouncementAsSeen(action.announcementId).pipe(
          map(() => UserActions.markAnnouncementAsSeenSuccess({ announcementId: action.announcementId })),
          catchError((error: HttpError) => of(UserActions.markAnnouncementAsSeenFailure({ error })))
        )
      )
    );
  });
}
